import type { ReactNode } from "react";
import { Download } from "lucide-react";
import { SkillResultMarkdown } from "@/components/organisms/studio/SkillResultMarkdown";
import { AgentArtifactList } from "@/components/organisms/studio/AgentArtifactList";
import { InlineArtifactPreview } from "@/components/organisms/studio/InlineArtifactPreview";
import { RunStatusBadge } from "@/components/organisms/studio/RunStatusBadge";
import { Button } from "@/components/atoms/studio/ui/button";
import { downloadReportAs } from "@/lib/studio/reportPdf";
import { runErrorLabel } from "@/lib/studio/utils";
import { ImproveInMultiAgentCTA } from "@/components/molecules/ImproveInMultiAgentCTA";
import type { AgentRunRecord, RunArtifact, RunStepRecord, RunStatusValue } from "@/services/studio/types";
import type { RunUsage } from "@/stores/agentRunStore";

// Single source of truth for the run report — rendered identically after a run
// completes (AgentRunTab), from Run History, and from the Reports page. The report
// is intentionally just the RESULT + the FILES: the run's own markdown answer and
// the downloadable artifacts. Run metadata / step tables / raw evidence are not
// shown here (they live in the JSON evidence artifact for anyone who wants them).
export function AgentRunReportView({
  run,
  artifacts,
  improving,
  onImprove,
}: {
  run: AgentRunRecord;
  artifacts: RunArtifact[];
  steps: RunStepRecord[];
  usage?: RunUsage | null;
  improving: boolean;
  onImprove: () => void;
}) {
  const title = run.surface_name || run.agent_id;
  // A run that ended in error carries its reason in `error_message`. It used to be
  // parsed and dropped, so the whole surface said was "ERROR" + "No report was
  // saved" — true, and useless. Shown above the body so it reads as the reason for
  // whatever follows: nothing at all for a hard failure, or the report itself for a
  // soft error (`generated_files_missing`), where the caveat belongs beside it.
  const errorLabel = runErrorLabel(run.error_message);
  // The PDF stays downloadable (Downloads) but is excluded from the inline preview —
  // an embedded PDF iframe is noise in the report body.
  const previewArtifacts = artifacts.filter((a) => !a.name.toLowerCase().endsWith(".pdf"));

  return (
    <div className="space-y-4">
      {/* ── Weave CTA: sent to the top so deeper analysis is one tap away ───── */}
      {(run.result_md || artifacts.length > 0) && (
        <ImproveInMultiAgentCTA improving={improving} onImprove={onImprove} />
      )}

      {/* ── Result: the answer, first and prominent ────────────────────────── */}
      <div className="rounded-xl border border-border bg-card overflow-hidden">
        <div className="flex items-center justify-between gap-3 border-b border-border bg-muted/20 px-5 py-3">
          <RunStatusBadge status={run.status as RunStatusValue} />
          {run.result_md && (
            <div className="flex shrink-0 items-center gap-1.5">
              <span className="mr-0.5 hidden font-mono text-[10px] uppercase tracking-wide text-muted-foreground/60 sm:inline">Download</span>
              <Button type="button" variant="outline" size="sm" onClick={() => downloadReportAs(run.result_md || "", "md", title)}>
                <Download className="mr-1.5 h-3.5 w-3.5" />MD
              </Button>
              <Button type="button" variant="outline" size="sm" onClick={() => downloadReportAs(run.result_md || "", "pdf", title)}>
                <Download className="mr-1.5 h-3.5 w-3.5" />PDF
              </Button>
              <Button type="button" variant="outline" size="sm" onClick={() => downloadReportAs(run.result_md || "", "docx", title)}>
                <Download className="mr-1.5 h-3.5 w-3.5" />Word
              </Button>
            </div>
          )}
        </div>
        <div className="p-5">
          {errorLabel && (
            <p className={`font-mono text-sm text-destructive${run.result_md ? " mb-4" : ""}`}>{errorLabel}</p>
          )}
          {run.result_md ? (
            <SkillResultMarkdown content={run.result_md} isStreaming={false} />
          ) : errorLabel ? null : (
            <p className="font-mono text-xs text-muted-foreground">No report was saved for this run.</p>
          )}
        </div>
      </div>

      {/* ── Files ───────────────────────────────────────────────────────────── */}
      {previewArtifacts.length > 0 && (
        <SectionCard title="Files" meta={`${previewArtifacts.length} file${previewArtifacts.length === 1 ? "" : "s"}`}>
          <InlineArtifactPreview runId={run.id} artifacts={previewArtifacts} orgId={run.org_id} sourceSurface="agent" />
        </SectionCard>
      )}
      {artifacts.length > 0 && (
        <SectionCard title="Downloads" meta={`${artifacts.length} file${artifacts.length === 1 ? "" : "s"}`}>
          <AgentArtifactList runId={run.id} artifacts={artifacts} variant="rows" orgId={run.org_id} />
        </SectionCard>
      )}
    </div>
  );
}

function SectionCard({ title, meta, children }: { title: string; meta?: string; children: ReactNode }) {
  return (
    <div className="overflow-hidden rounded-xl border border-border bg-card">
      <div className="flex items-center justify-between border-b border-border bg-muted/20 px-5 py-3">
        <span className="font-mono text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">{title}</span>
        {meta && <span className="font-mono text-[10px] text-muted-foreground/60">{meta}</span>}
      </div>
      <div className="p-5">{children}</div>
    </div>
  );
}
